import { Host, Element, Component, Event, EventEmitter, h, Prop } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { GreyBoxSize } from './grey-box.types';

/**
 * Internal component
 * @slot - Contents placed inside grey box
 */
@Component({
  tag: 'wb-grey-box',
  styleUrl: 'grey-box.scss',
  scoped: true,
})
export class GreyBox {
  @Element()
  host!: HTMLElement;

  /** The size of the grey box */
  @Prop({ reflect: true })
  public size?: GreyBoxSize = 'l';

  /** Marks grey box as successful */
  @Prop({ reflect: true })
  public success?: boolean;

  /** Marks control as invalid. automatically set by slotted wb-control-error */
  @Prop({ reflect: true, mutable: true })
  public error?: boolean;

  /** Sets state as open for visual changes */
  @Prop({ reflect: true, mutable: true })
  public open: boolean = false;

  /** Marks grey box as successful */
  @Prop({ reflect: true, mutable: true })
  public active: boolean = false;

  /** If present, indicates that the input should automatically have focus when the page has finished loading */
  @Prop({ reflect: true, attribute: 'autofocus' })
  public autoFocus?: boolean = false; // stencil didn't like when the property had the name of `autofocus`

  /**
   * Makes the input component disabled.
   * This prevents users from being able to interact with the component, and conveys its inactive state to assistive technologies.
   */
  @Prop({ reflect: true })
  public disabled?: boolean = false;

  /** Prevents the value from being editable. */
  @Prop({ reflect: true })
  public readonly?: boolean = false;

  /** Display caret icon */
  @Prop({ reflect: true })
  public hasCaretIcon?: boolean = false;

  /** aria-label for caret icon. It's required when hasCaretIcon is provided */
  @Prop({ reflect: true, mutable: true })
  public buttonActionLabel?: string;

  /** Display status icons for error or success state */
  @Prop({ reflect: true })
  public hasStatusIcons?: boolean = false;

  /** label describing to a screenreader the state of a validation */
  @Prop({ reflect: true })
  public readerValidationMessage?: string = '';

  /** Emitted when the button gets clicked */
  @Event()
  public wbgreyboxbuttonclick: EventEmitter<object>;

  private handleButtonClick = (): void => {
    this.wbgreyboxbuttonclick.emit({ isOpen: this.open });
  };

  private TagNames = transformTagNames(this, 'wb-grey-box', ['wb-round-button', 'wb-icon', 'wb-visually-hidden', 'wb-badge']);

  public render(): JSX.Element {
    const { WbRoundButton, WbIcon, WbVisuallyHidden, WbBadge } = this.TagNames;

    return (
      <Host class="wb-grey-box">
        <slot />
        {this.hasCaretIcon && (
          <WbRoundButton data-testid="wb-grey-box-button" size={`new-${this.size}`} class="caret-icon" onClick={this.handleButtonClick} tabindex="-1" button-aria-label={this.buttonActionLabel}>
            <WbIcon name="caret-down/16" />
          </WbRoundButton>
        )}
        {this.hasStatusIcons && (
          <span aria-live="polite">
            {this.success && <WbBadge variant="success" class="status-icon status-icon--success" />}
            {this.error && <WbBadge variant="error" class="status-icon status-icon--error" />}
            {this.readerValidationMessage && <WbVisuallyHidden>{this.readerValidationMessage}</WbVisuallyHidden>}
          </span>
        )}
      </Host>
    );
  }
}
